import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';

import { finalizeInteraction } from '@ayeldo/frontend-core/auth/idpProxy';
import { isSafeRedirectUrl } from '@ayeldo/frontend-core/auth/redirectGuard';
import { resolveAuthAllowedRedirectHosts } from '@ayeldo/frontend-core/auth/resolveAuthAllowedRedirectHosts';

// Resolve the allow-list once at module load time so it is stable across renders.
const allowedHosts = resolveAuthAllowedRedirectHosts(import.meta.env);

const DEFAULT_LOCAL_IDP_URL = 'http://localhost:3010';

function resolveIdpApiBaseUrl(): string {
  const configured: unknown = import.meta.env.VITE_API_URL;
  const trimmed = typeof configured === 'string' ? configured.trim() : '';
  if (trimmed !== '') return trimmed;
  if (import.meta.env.DEV) return DEFAULT_LOCAL_IDP_URL;
  throw new Error('VITE_API_URL is not configured for production build');
}

function hasInteractionId(interaction: string | null): interaction is string {
  return interaction !== null && interaction.trim().length !== 0;
}

/**
 * OauthFinalizePage — React proxy route for /oauth/finalize.
 *
 * After the user authenticates at /auth/signin, the IDP redirects here with
 * an interaction uid. This page POSTs to finalize the interaction and
 * navigates to the returned OIDC redirect URI.
 */
export function OauthFinalizePage() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const interaction = searchParams.get('interaction');

  useEffect(() => {
    if (!hasInteractionId(interaction)) {
      setStatus('error');
      setErrorMessage('Missing interaction parameter. Please try signing in again.');
      return;
    }

    let cancelled = false;

    const doFinalize = async () => {
      try {
        const idpApiBase = resolveIdpApiBaseUrl();
        const result = await finalizeInteraction(interaction, { idpApiBase });
        if (!cancelled) {
          if (!isSafeRedirectUrl(result.redirectUri, allowedHosts)) {
            // eslint-disable-next-line no-console
            console.warn('OauthFinalizePage: rejected redirect target', result.redirectUri);
            setStatus('error');
            setErrorMessage('Redirect blocked for safety. Please try again or contact support.');
            return;
          }
          window.location.href = result.redirectUri;
        }
      } catch (err) {
        if (!cancelled) {
          setStatus('error');
          setErrorMessage(err instanceof Error ? err.message : 'Failed to complete sign-in.');
        }
      }
    };

    void doFinalize();

    return () => {
      cancelled = true;
    };
  }, [interaction]);

  if (status === 'loading') {
    return (
      <Box
        sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <Stack spacing={2} alignItems="center">
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            Please wait...
          </Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <Stack spacing={2} alignItems="center" sx={{ maxWidth: 400, textAlign: 'center' }}>
        <Typography variant="h5" component="h1">
          Sign-in Error
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {errorMessage ?? 'Failed to complete sign-in. Please try again.'}
        </Typography>
      </Stack>
    </Box>
  );
}
